"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { MobileBottomNav } from "../components/MobileBottomNav"
import { useAuth } from "../hooks/useAuth"
import Footer from "../components/Footer"
import { motion, useScroll, useTransform } from "framer-motion"

export default function AboutUsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const { scrollYProgress } = useScroll()
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0])
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  }

  const teamMembers = [
    {
      name: "Rahul Sharma",
      role: "Founder & CEO",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      bio: "Rahul founded VillaKosh with a vision to transform the way people experience luxury stays in India.",
    },
    {
      name: "Priya Patel",
      role: "Chief Operating Officer",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      bio: "Priya oversees all operations and ensures that every VillaKosh property meets our high standards.",
    },
    {
      name: "Vikram Singh",
      role: "Head of Technology",
      image: "https://randomuser.me/api/portraits/men/68.jpg",
      bio: "Vikram leads our tech team, building innovative solutions to enhance the booking experience.",
    },
    {
      name: "Ananya Desai",
      role: "Marketing Director",
      image: "https://randomuser.me/api/portraits/women/65.jpg",
      bio: "Ananya crafts our brand strategy and helps property owners showcase their villas effectively.",
    },
  ]

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header */}
      {isMobile ? (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold">About Us</h1>
          </div>
        </header>
      ) : (
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold">VillaKosh</h1>
              </Link>
              <nav className="flex items-center space-x-4">
                <Link to="/" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Home
                </Link>
                <Link to="/about" className="bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  About
                </Link>
                <Link to="/contact" className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out">
                  Contact
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Hero Section */}
      <motion.div
        className="relative h-[50vh] bg-cover bg-center"
        style={{
          backgroundImage: "url('https://cdn.pixabay.com/photo/2024/04/26/18/36/farm-house-8722429_1280.jpg')",
          opacity,
          scale,
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">About VillaKosh</h1>
            <p className="text-xl md:text-2xl max-w-2xl mx-auto">
              Discover the story behind India's premier villa booking platform
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.main
        className={`container mx-auto px-4 py-12 ${isMobile ? "pb-24" : ""}`}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="max-w-4xl mx-auto">
          <motion.section className="mb-16" variants={itemVariants}>
            <h2 className="text-3xl font-bold mb-6 text-center">Our Story</h2>
            <div className="bg-white rounded-lg shadow-md p-8">
              <p className="text-gray-700 mb-4">
                Founded in 2020, VillaKosh was born from a simple idea: to make luxury villa stays accessible to
                everyone while helping property owners maximize their potential. What started as a small venture with
                just 10 properties has now grown into India's leading platform for premium vacation rentals.
              </p>
              <p className="text-gray-700 mb-4">
                Our journey began when our founder, Rahul Sharma, struggled to find a seamless booking experience for a
                family vacation in Goa. Frustrated by fragmented listings, unreliable information, and hidden fees, he
                envisioned a platform that would bring transparency, trust, and exceptional service to the villa rental
                market.
              </p>
              <p className="text-gray-700">
                Today, VillaKosh connects thousands of travelers with their dream vacation homes across India. We've
                expanded from beach villas to mountain retreats, heritage properties, and urban luxury apartments - all
                while maintaining our commitment to quality and customer satisfaction.
              </p>
            </div>
          </motion.section>

          <motion.section className="mb-16" variants={itemVariants}>
            <h2 className="text-3xl font-bold mb-6 text-center">Our Mission</h2>
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
                  <img
                    src="https://cdn.pixabay.com/photo/2016/11/18/17/46/house-1836070_1280.jpg"
                    alt="Luxury villa"
                    className="rounded-lg shadow-md w-full h-auto"
                  />
                </div>
                <div className="md:w-1/2">
                  <p className="text-gray-700 mb-4">
                    At VillaKosh, our mission is to transform the way people experience luxury stays. We believe that
                    everyone deserves access to exceptional accommodations that feel like home, but better.
                  </p>
                  <p className="text-gray-700 mb-4">
                    For travelers, we strive to curate the finest properties and provide a booking experience that's
                    transparent, reliable, and stress-free.
                  </p>
                  <p className="text-gray-700">
                    For property owners, we offer a platform to showcase their unique spaces to a wider audience,
                    maximize occupancy, and receive fair compensation for their hospitality.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section className="mb-16" variants={itemVariants}>
            <h2 className="text-3xl font-bold mb-6 text-center">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-center">Trust & Transparency</h3>
                <p className="text-gray-600 text-center">
                  We believe in honest listings, clear pricing, and open communication between guests and hosts.
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-center">Excellence</h3>
                <p className="text-gray-600 text-center">
                  We curate only the finest properties and strive for exceptional service at every touchpoint.
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-center">Community</h3>
                <p className="text-gray-600 text-center">
                  We foster meaningful connections between travelers and local communities across India.
                </p>
              </div>
            </div>
          </motion.section>

          <motion.section variants={itemVariants} className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-center">Our Team</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <img
                    src={member.image || "/placeholder.svg"}
                    alt={member.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                    <p className="text-blue-600 mb-3">{member.role}</p>
                    <p className="text-gray-600 text-sm">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2 className="text-3xl font-bold mb-6 text-center">Our Impact</h2>
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                  <p className="text-gray-700">Premium Properties</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
                  <p className="text-gray-700">Happy Guests</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-blue-600 mb-2">20+</div>
                  <p className="text-gray-700">Cities Across India</p>
                </div>
              </div>
              <div className="mt-8">
                <p className="text-gray-700 text-center">
                  Beyond numbers, we take pride in supporting local economies, promoting sustainable tourism, and
                  creating memorable experiences for families and friends across the globe.
                </p>
              </div>
            </div>
          </motion.section>
        </div>
      </motion.main>

      {/* Footer for desktop */}
      {!isMobile && <Footer />}

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav user={user} />}
    </div>
  )
}

